import { Text, Flex, Button, useToast } from '@chakra-ui/react';
import { PageContent } from 'src/components/PageContent';
import { SectionBox } from 'src/components/SectionBox';
import { GetParamsObject } from '@/model/query-params';
import { breadcrumb } from './breadcrumb';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { useFiltersForm } from '@/utils/useFiltersForm';
import { CategoryVideoFilterBox } from './components/CategoryVideoFilterBox';
import { useCategoryVideo, useDeleteCategoryVideoMutation, useGetCategoryVideoById } from '@/services/queries/category-video';
import { useCallback, useState } from 'react';
import { CategoryVideo } from '@/model';
import { CategoryVideoFilters } from './types';
import { CategoryVideoTable } from './components/CategoryVideoTable/CategoryVideoTable';
import { ConfirmationModal } from '@/components/ConfirmationModal';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { CategoryVideoEditModal } from './components/CategoryVideoEditModal';
import { CategoryVideoCreateModal } from './components/CategoryVideoCreateModal';
import { useTranslation } from 'react-i18next';

export const ApplicationCategoryVideo = () => {
  const toast = useToast();
  const { t } = useTranslation();

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const [selectedItem, setSelectedItem] = useState<CategoryVideo | undefined>(undefined);

  const deleteMutation = useDeleteCategoryVideoMutation();
  const getItemByUUID = useGetCategoryVideoById();

  const filtersForm = useFiltersForm<CategoryVideoFilters>({
    defaultValues: {
      title: undefined,
      start_date: undefined,
      end_date: undefined,
    },
  });
  const filterData = filtersForm.watch();

  const [sorting, setSorting] = useState<{ field: string; desc: boolean }>({ field: 'created_at', desc: true });
  const [pagination, setPagination] = useState<{ pageNumber: number; pageSize: number }>({
    pageNumber: 1,
    pageSize: 10,
  });

  const params = GetParamsObject(
    filterData,
    pagination.pageNumber,
    pagination.pageSize,
    sorting.desc ? 'desc' : 'asc',
    sorting.field,
  );

  const { data: itemData, isLoading, forceRefetch } = useCategoryVideo(params);

  const itemsData = itemData?.data || [];

  const onHandleDelete = (item: CategoryVideo) => {
    setSelectedItem(item);
    setIsConfirmModalOpen(true);
  };

  const onHandleDeleteConfirm = () => {
    if (!selectedItem?.uuid) return;

    setIsConfirmModalOpen(false);

    deleteMutation.mutate(selectedItem.uuid, {
      onSuccess: () => {
        toast({
          title: t('pages.application_category_video.category_video_toast_success'),
          status: 'success',
        });
        forceRefetch();
        setSelectedItem(undefined);
      },
      onError: (resp: any) => {
        setSelectedItem(undefined);
        toast({
          status: 'error',
          title: (resp && resp.message) || t('pages.application_category_video.category_video_toast_error'),
        });
      },
    });
  };

  const onHandleEdit = (uuid: string) => {
    getItemByUUID.mutate(uuid, {
      onSuccess: res => {
        setSelectedItem(res);
        setIsEditModalOpen(true);
      },
      onError: (resp: any) => {
        toast({
          status: 'error',
          title: (resp && resp.message) || t('pages.application_category_video.category_video_edit_toast_error'),
        });
      },
    });
  };

  const onHandleEditDone = useCallback(() => {
    setIsEditModalOpen(false);
    setSelectedItem(undefined);
    forceRefetch();
  }, [forceRefetch]);

  const onHandleCreateDone = useCallback(() => {
    setIsCreateModalOpen(false);
    forceRefetch();
  }, [forceRefetch, setIsCreateModalOpen]);

  return (
    <PageContent title={t('pages.application_category_video.category_video_page_title')} breadcrumbItems={breadcrumb}>
      <SectionBox bodyProps={{ gap: 'small' }}>
        <Flex direction={{ base: 'column', md: 'row' }} justifyContent={{ md: 'space-between' }} gap={4}>
          <Flex alignItems='center' gap={4}>
            <Text fontWeight='bold' color='brand.500'>
              {t('pages.application_category_video.category_video_section_title')}
            </Text>
          </Flex>

          <Button
            display='flex'
            alignItems='center'
            gap={2}
            onClick={() => setIsCreateModalOpen(true)}
            width={{ base: '100%', md: 'initial' }}
          >
            <ChakraFeatherIcon name='plus-circle' />
            {t('pages.application_category_video.category_video_add_button')}
          </Button>
        </Flex>
        <SectionBox bodyProps={{ gap: 'small' }}>
          <CategoryVideoFilterBox filtersForm={filtersForm} forceRefetch={forceRefetch} />
        </SectionBox>

        <Flex direction='column' gap='deka' mt={8}>
          <CategoryVideoTable
            data={itemsData}
            isLoading={isLoading}
            sorting={sorting}
            pageNumber={pagination.pageNumber}
            pageSize={pagination.pageSize}
            totalPages={itemData?.meta.pages || 0}
            totalItems={itemData?.meta.total || 0}
            onSortingChange={(field, desc) => setSorting({ field, desc })}
            onPaginationChange={(pageNumber, pageSize) => setPagination({ pageNumber, pageSize })}
            onEditClick={item => onHandleEdit(item.uuid)}
            onDeleteClick={item => onHandleDelete(item)}
          />
        </Flex>
      </SectionBox>

      {selectedItem && (
        <CategoryVideoEditModal
          isOpen={!!isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedItem(undefined);
          }}
          item={selectedItem}
          onDone={onHandleEditDone}
        />
      )}

      <CategoryVideoCreateModal
        isOpen={!!isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onDone={onHandleCreateDone}
      />

      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        onClose={() => {
          setIsConfirmModalOpen(false);
          setSelectedItem(undefined);
        }}
        onConfirm={onHandleDeleteConfirm}
        isLoading={false}
        title={t('pages.application_category_video.category_video_title')}
        description={
          <Text>
            {t('pages.application_category_video.category_video_description')}{' '}
            <Text as='span' fontWeight='bold'>
              "{selectedItem?.title}"
            </Text>
            ?
          </Text>
        }
      />

      <CenteredSpinner isLoading={getItemByUUID.isLoading || deleteMutation.isLoading} />
    </PageContent>
  );
};
